import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Phone } from "lucide-react";
import { BRAND, HERO_IMG } from "@/data";

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.14 },
  }),
};

const HEAD_LINES = ["BUILDING.", "ENHANCING.", "DELIVERING."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden bg-[#14171A]"
      data-testid="hero-section"
    >
      {/* Parallax image background */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={HERO_IMG}
          alt="Groundworks excavator on site"
          className="h-full w-full object-cover opacity-[0.18]"
        />
        <div className="absolute inset-0 bg-[#14171A]/40" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 min-h-screen flex flex-col justify-center pt-28 pb-16"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-body text-sm uppercase tracking-[0.4em] text-[#7AB648] mb-6 md:mb-10"
          data-testid="hero-eyebrow"
        >
          Gormans Contracting — Est. Local
        </motion.p>

        <h1 className="font-display uppercase leading-[0.9] text-white">
          {HEAD_LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineReveal}
                initial="hidden"
                animate="visible"
                className="block text-[15vw] md:text-[9vw] lg:text-[8rem]"
                style={
                  i === 2
                    ? { WebkitTextStroke: "1.5px #7AB648", color: "transparent" }
                    : {}
                }
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="max-w-md font-body text-base md:text-lg text-[#A8AEB2] leading-relaxed">
            Roofing, landscaping, groundworks &amp; more. Trusted local
            craftsmanship that builds, enhances and delivers — every single time.
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <a
              href="#bookings"
              className="group inline-flex items-center gap-2 rounded-sm bg-[#7AB648] px-7 py-4 font-head font-bold uppercase tracking-wide text-[#14171A] transition-colors hover:bg-[#6BA339] active:scale-95"
              data-testid="hero-cta-button"
            >
              Book a Job
              <ArrowDownRight size={20} className="transition-transform group-hover:translate-x-1 group-hover:translate-y-1" />
            </a>
            <a
              href={BRAND.phoneHref}
              className="inline-flex items-center gap-2 rounded-sm border border-[#A8AEB2]/30 px-7 py-4 font-head font-bold uppercase tracking-wide text-white transition-colors hover:border-[#7AB648] hover:text-[#7AB648] active:scale-95"
              data-testid="hero-call-button"
            >
              <Phone size={18} />
              {BRAND.phone}
            </a>
          </div>
        </motion.div>
      </motion.div>

      {/* Bottom subline strip */}
      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-[#A8AEB2]/15 bg-[#14171A]/60 backdrop-blur-sm">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4">
          <p className="font-head text-xs md:text-sm uppercase tracking-[0.3em] text-[#A8AEB2]">
            {BRAND.subline}
          </p>
        </div>
      </div>
    </section>
  );
}
